import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Spin } from 'antd';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { useModalState } from '../../hooks/modalState';
import {
    changePasswordAction,
    changeSubscriptionAdminAction,
    deleteUserAction,
    getAccountAdminsAction,
    getAccountSettingsAction,
    getSubscriptionAdminAction,
    getUserByIdAction,
    getUserReferencesAction,
    resetUserPasswordAction,
    updateUserAction,
} from '../../redux/actions/userActions';
import {
    ChangePasswordRequest,
    CreateUserRequest,
    GetAccountSettingsResponse,
    GetSubscriptionAdminResponse,
    UserByIdResponse,
} from '../../redux/actions/userActions/types';
import { alertService } from '../../services';
import { addPage, removePage } from '../../redux/reducers/navigationReducer/NavigationSlice';
import { clearState } from '../../redux/persistedState';
import { riseStoredUser, setStoredUser } from '../../redux/reducers/authReducer/AuthSlice';
import { extractGetByIdUser, getAccountRole, getAccountUser } from '../../helpers/extract';
import { SelectData } from '../../models/common/types';
import { Margin, PrimaryButton, SpaceEnd, Text } from '../../styles/common.styles';
import BasicInformationTab from './BasicInformationTab';
import PageHeader from '../../components/common/pageHeader/PageHeader';
import CustomTabs from '../../components/common/tabs/CustomTabs';
import DeleteModal from '../../components/common/deleteModal/DeleteModal';
import ConfirmSubscriptionAdminModal from '../../components/common/confirmSubscriptionAdminModal/ConfirmSubscriptionAdminModal';
import { AccountChangePasswordButton } from '../account/Account.styles';
import ResetPasswordModal from '../../components/common/resetModal/ResetModal';
import { AlertMessages } from '../../components/alert/AlertMessages';
import ErrorModal from '../../components/common/errorModal/ErrorModal';
import { ActionResponse } from '../../models/actionResponse/types';
import { AccountPermissionTypes } from '../../models/permission/enums';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { BasicInfoColumn, BasicInformationTitle, ChangeAdminRow, InlineLink, SubscriptionList, SubscriptionListItem } from './BasicInformation.styles';
import { BluedText } from '../users/UsersPage.styles';
import CustomSelect from '../../components/common/select/CustomSelect';
import { blueDark01 } from '../../styles/colors';

interface EditParams {
    id: string;
}

const DOCUMENTATION_LINK="https://transparencycatalog.zendesk.com/hc/en-us/sections/11967212657298-Account-types-Project-roles";

const EditAccountPage: React.FC = () => {
    const { id } = useParams<EditParams>();
    const history = useHistory();
    const selector = useAppSelector((state) => state.authReducer);
    const { user: storedUser } = selector;
    const dispatch = useAppDispatch();
    const [user, setUser] = useState<CreateUserRequest | undefined>(undefined);
    const [accountRole, setAccountRole] = useState<AccountPermissionTypes>();
    const [canAddToOrgFavorites, setCanAddToOrgFavorites] = useState<boolean>(false);
    const [tagPrivilege, setTagPrivilege] = useState<boolean>(false);
    const [userSubscriptionType, setUserSubscriptionType] = useState('');
    const [subscriptionOwnerName, setSubscriptionOwnerName] = useState<string>('');
    const [accountAdmins, setAccountAdmins] = useState<Array<{ id: string; name: string; email: string }>>([]);
    const [selectedNewAdmin, setSelectedNewAdmin] = useState<string>('');
    const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
    const { onOpen, onClose, isOpen } = useModalState();
    const [subscriptionAdmin, setSubscriptionAdmin] = useState<any>(null);
    const errorModal = useModalState();
    const confirmAdminChangeModal = useModalState();
    const [selectData, setSelectedData] = useState({
        roleName: [],
        projectGroup: [{ id: '', name: ' ' }],
        allTitleRoles: [],
        allAccountRoles: [],
        allOrganizationSizes: [],
    });


    useEffect(() => {
        getData();
        return () => {
            dispatch(removePage());
        };
    }, []);

    const getSubscriptionOwner = async () => {
        try {
            const result = (await dispatch(getSubscriptionAdminAction())) as GetSubscriptionAdminResponse;
            const parsed = result?.payload;
            const contact = parsed?.data;
            if (contact) {
                const normalized = {
                    firstName: contact.firstName || '',
                    lastName: contact.lastName || '',
                    email: contact.email || null,
                    phoneNumber: contact.phoneNumber || null,
                    phoneNumberExt: contact.phoneNumberExt || null,
                };
                setSubscriptionAdmin(normalized);
            }
        } catch (err) {
            console.warn('Failed to load subscription admin', err);
        }
    };

    const getData = async () => {
        await getUser();
        await getDataForSelect();
        await getAccountSettings();
        await getAccountAdmins();
        await getSubscriptionOwner();
    };



    const getDataForSelect = async () => {
        const { payload } = (await dispatch(getUserReferencesAction())) as any;
        if (payload?.data) {
            setSelectedData({
                roleName: payload.data.allRoles || [],
                projectGroup: selectData.projectGroup.concat(payload.data.allProjectGroups),
                allTitleRoles: payload.data.allTitleRoles || [],
                allAccountRoles:
                    payload.data.allAccountRoles.map((role: SelectData) => ({
                        ...role,
                        name: role.name.replace('Account', ''),
                    })) || [],
                allOrganizationSizes: payload.data.allOrganizationSizes || [],
            });
        } else {
            errorModal.onOpen();
        }
    };

    const getAccountSettings = async () => {
        const { payload } = (await dispatch(
            getAccountSettingsAction(),
        )) as GetAccountSettingsResponse;
        if (payload?.data?.contactName) {
            setSubscriptionOwnerName(payload.data.contactName);
        }
    };

    const getAccountAdmins = async () => {
        const { payload } = (await dispatch(getAccountAdminsAction())) as any;
        if (payload?.data && Array.isArray(payload.data)) {
            const admins = payload.data.map((admin: any) => ({
                id: admin.email,
                name: `${admin.firstName} ${admin.lastName}`,
                email: admin.email,
            }));
            setAccountAdmins(admins);
        }
    };

    const handleAdminChange = (newAdminEmail: string) => {
        if (newAdminEmail && user?.email !== newAdminEmail) {
            setSelectedNewAdmin(newAdminEmail);
            confirmAdminChangeModal.onOpen();
        }
    };

    const handleChangeSubscriptionAdmin = async () => {
        if (!selectedNewAdmin || user?.email === selectedNewAdmin) {
            return;
        }

        const { payload } = await dispatch(
            changeSubscriptionAdminAction({ newAdminEmail: selectedNewAdmin })
        ) as any;

        if (payload?.message) {
            alertService.success('Subscription Admin changed successfully');
            const { payload } =  await dispatch(getUserByIdAction(selector.user?.id.toString() || '')) as ActionResponse<Array<UserByIdResponse>> ;
            dispatch(setStoredUser(payload?.data[0]));
            window.location.reload();
        }
    };

    const getUser = async () => {
        const { payload } = (await dispatch(getUserByIdAction(id))) as ActionResponse<
            Array<UserByIdResponse>
        >;
        if (payload?.data[0] && selector.user?.id) {
            setUserSubscriptionType(payload.data[0].subscription.planName);

            setAccountRole(
                getAccountRole(
                    payload.data[0].accountUser, payload.data[0].parentAccountId
                ).accountRole
            );
            setCanAddToOrgFavorites(
                getAccountUser(
                    payload.data[0].accountUser,
                    payload.data[0].parentAccountId
                ).canAddProductsToOrgFavorites
            );
            setTagPrivilege(
                getAccountUser(
                    payload.data[0].accountUser,
                    payload.data[0].parentAccountId
                ).tagPrivilege
            );

            const currentUser = extractGetByIdUser(payload?.data[0]);
            Object.keys(currentUser).forEach((key) => {
                if (!currentUser[key]) {
                    delete currentUser[key];
                }
            }, currentUser);
            setUser(currentUser);
            dispatch(
                addPage({
                    key: `/users/edit/${payload.data[0]?.id}`,
                    value: `${payload.data[0]?.firstName} ${payload.data[0]?.lastName}`,
                }),
            );
        } else {
            history.push('/users');
        }
    };
    const onSubmit = async (data: CreateUserRequest) => {

        Object.keys(data).forEach((key) => {
            if (!data[key]) {
                data[key] = null;
            }
        }, data);
        const { payload } = (await dispatch(
            updateUserAction({
                user: {
                    ...data,
                    userName: data.email,
                    accountId: user?.parentAccountId || user?.accountId,
                    canAddProductsToOrgFavorites: canAddToOrgFavorites,
                    tagPrivilege: tagPrivilege,
                },
                id,
            }),
        )) as any;
        if (payload?.data) {
            if (
                payload?.data[0].id === selector.user?.id &&
                payload?.data[0].userName !== user?.userName
            ) {
                clearState();
                history.push('/login');
                dispatch(riseStoredUser());
                alertService.info('Please login with a new email', {
                    autoClose: false,
                });
                return;
            }
            if (payload.data[0].id === selector.user?.id) {
                dispatch(setStoredUser(payload?.data[0]));
            }
            alertService.success(AlertMessages.UPDATE_USER);

            setTimeout(() => {
                history.push('/users/edit');
            }, 500);
        } else {
            errorModal.onOpen();
        }
    };

    const openDeleteModal = () => {
        setIsOpenDeleteModal(true);
    };

    const deleteUser = async () => {
        const { payload } = (await dispatch(deleteUserAction(id))) as any;
        if (payload?.message) {
            history.push('/users/edit');
        }
    };

    const submitContainer = (callback: () => void) => {
        return (
            <SpaceEnd>
                <PrimaryButton htmlType="submit" onClick={callback}>
                    Save
                </PrimaryButton>
                {id !== selector?.user?.id.toString() && (
                    <AccountChangePasswordButton onClick={openDeleteModal}>
                        Delete account
                    </AccountChangePasswordButton>
                )}
            </SpaceEnd>
        );
    };

    const submitPassword = async (data: ChangePasswordRequest) => {
        const { payload } = await dispatch(changePasswordAction(data));
        if (payload.message) {
            alertService.success(AlertMessages.CHANGE_PASSWORD);
        }
    };

    const handleCanAddToOrgFavChange = (e: CheckboxChangeEvent) => {
        setCanAddToOrgFavorites(e.target.checked);
    }

    const handleTagPrivilegeChange = (e: CheckboxChangeEvent) => {
        setTagPrivilege(e.target.checked);
    }

    const informationCards = useMemo(() => {
        const subAdminIsUser = (storedUserEmail: string | undefined, userEmail: string | undefined, subscriberAdminEmail: string | undefined) => {
            return storedUserEmail === userEmail && userEmail === subscriberAdminEmail;
        }

        if (!subscriptionAdmin) {
            return <BasicInfoColumn style={{ maxWidth: "470px", flex: 2 }}>
                <BasicInformationTitle style={{ width: "100%" }}>
                    Subscription owner
                </BasicInformationTitle>
            </BasicInfoColumn>;
        }
        return <BasicInfoColumn style={{ maxWidth: "470px", flex: 2 }}>
            <BasicInformationTitle style={{ width: "100%" }}>
                Subscription owner
            </BasicInformationTitle>
            <Margin value="0 0 28px 0" />
            {subAdminIsUser(storedUser?.email, user?.email, subscriptionAdmin?.email) ? (
                <Text weight={400} size={13} height={16} color={blueDark01}>You are the <BluedText href={DOCUMENTATION_LINK}>Subscription Admin</BluedText> for Project Builder & Library. Your organization's subscription is connected to your name and email address.
                    <SubscriptionList style={{ marginTop: '8px', marginLeft: '-8px'}}>
                        <SubscriptionListItem>There is only one Subscription Admin.</SubscriptionListItem>
                        <SubscriptionListItem>The Subscription Admin's account cannot be deleted.</SubscriptionListItem>
                        <SubscriptionListItem>
                            You can reassign the Subscription Admin function to another Account Admin.
                        </SubscriptionListItem>
                        <SubscriptionListItem>Only an Account Admin can become the Subscription Admin.</SubscriptionListItem>
                    </SubscriptionList>
                    <ChangeAdminRow>
                        <CustomSelect
                            label={"Change Subscription Admin"}
                            block
                            options={accountAdmins.filter((admin) =>
                                admin.email !== subscriptionAdmin?.email)
                                .map((admin) => ({
                                    id: admin.email,
                                    name: admin.name,
                                }))}
                            value={selectedNewAdmin === '' ? null : selectedNewAdmin}
                            placeholder={'Select new Subscription Admin'}
                            props={{
                                onChange: (value: string) => handleAdminChange(value),
                            }}
                            style={{ width: '354px' }}
                        />
                    </ChangeAdminRow>
                    <Text weight={400} size={13} height={16} color={blueDark01}>
                        Need help? <InlineLink href="mailTo:support@transparencycatalog.com">Contact Sustainable Minds.</InlineLink>
                    </Text>
                </Text>
            ) : (
                <Text weight={400} size={13} height={16} color={blueDark01}>
                    <BluedText href={`mailto:${subscriptionAdmin?.email}`}>
                        {subscriptionAdmin?.firstName} {subscriptionAdmin?.lastName}
                    </BluedText>{' '}
                    is the <BluedText href={DOCUMENTATION_LINK}>Subscription Admin</BluedText>, the business owner for this Project Builder & Library subscription. Contact for questions regarding your account, or for requesting more accounts.
                </Text>
            )}
        </BasicInfoColumn>
    },
        [accountAdmins, selectedNewAdmin, subscriptionAdmin, user?.email, storedUser]);

    const tabs = [
        {
            name: 'Basic information',
            tab: (
                <BasicInformationTab
                    user={user}
                    onSubmit={onSubmit}
                    submitContainer={submitContainer}
                    resetPassword={onOpen}
                    submitPassword={submitPassword}
                    isOwner={id === selector?.user?.id.toString()}
                    userSubscriptionType={userSubscriptionType}
                    accountRole={accountRole}
                    canAddToOrgFav={canAddToOrgFavorites}
                    handleCanAddToOrgFavChange={handleCanAddToOrgFavChange}
                    tagPrivilege={tagPrivilege}
                    handleTagPrivilegeChange={handleTagPrivilegeChange}
                    subscriptionAdminCard={informationCards}
                    informationCards={() => {
                        return <div style={{ flex: 1 }}></div>
                    }}
                />
            ),
            key: '1',
        },
    ];

    const resetPassword = async () => {
        const { payload } = (await dispatch(resetUserPasswordAction(id))) as any;
        if (payload.message) {
            window.scroll(0, 0);
            alertService.success(AlertMessages.RESET_PASSWORD);
            onClose();
        }
    };

    return (
        <>
            <PageHeader title={`${user?.firstName} ${user?.lastName}’s account`} />
            <Spin size="large" spinning={selector.pending || !subscriptionAdmin}>
                <CustomTabs tabs={tabs} defaultKey="1" />
            </Spin>
            <DeleteModal
                isOpen={isOpenDeleteModal}
                closeModal={() => {
                    setIsOpenDeleteModal(false);
                }}
                reload={deleteUser}
                text={`${user?.firstName} ${user?.lastName}`}
                title="user"
                icon="trash-can-list.svg"
            />
            <ResetPasswordModal
                isOpen={isOpen}
                text={`${user?.firstName} ${user?.lastName}`}
                closeModal={onClose}
                reload={resetPassword}
            />
            {errorModal.isOpen && (
                <ErrorModal isOpen={errorModal.isOpen} closeModal={errorModal.onClose} />
            )}
            {confirmAdminChangeModal.isOpen && (
                <ConfirmSubscriptionAdminModal
                    isOpen={confirmAdminChangeModal.isOpen}
                    closeModal={() => {
                        confirmAdminChangeModal.onClose();
                        setSelectedNewAdmin('');
                    }}
                    onConfirm={handleChangeSubscriptionAdmin}
                    newAdminName={
                        accountAdmins.find((admin) => admin.email === selectedNewAdmin)?.name || ''
                    }
                />
            )}
        </>
    );
};

export default EditAccountPage;
